import type { MeResponse } from "@safeplate/contracts";
import { Button, Card, CardContent } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, LockKeyhole } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { ApiError, displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { organisationChoices } from "../state/selection.js";
import {
  bindSharedDevice,
  enrollCurrentDeviceAccount,
  getSharedDeviceRoster,
  scopeMeToOrganisation,
  unbindSharedDevice,
} from "./quick-access-api.js";

export function SharedDeviceGate({
  children,
  me,
}: {
  children: (scopedMe: MeResponse) => ReactNode;
  me: MeResponse;
}) {
  const queryClient = useQueryClient();
  const organisations = organisationChoices(me);
  const roster = useQuery({
    queryFn: getSharedDeviceRoster,
    queryKey: ["shared-device-roster"],
    retry: false,
  });
  const bind = useMutation({
    mutationFn: bindSharedDevice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shared-device-roster"],
      });
    },
  });
  const enroll = useMutation({
    mutationFn: enrollCurrentDeviceAccount,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["shared-device-roster"] });
    },
  });
  const remove = useMutation({
    mutationFn: unbindSharedDevice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shared-device-roster"],
      });
    },
  });
  const missingBinding =
    roster.error instanceof ApiError && roster.error.status === 401;

  const belongsHere = Boolean(
    roster.data &&
      organisations.some(
        (organisation) => organisation.id === roster.data?.org_id,
      ),
  );

  useEffect(() => {
    if (belongsHere && !enroll.isPending && !enroll.isSuccess && !enroll.error) {
      enroll.mutate();
    }
  }, [belongsHere, enroll.error, enroll.isPending, enroll.isSuccess, enroll.mutate]);

  useEffect(() => {
    const only = organisations[0];
    if (
      missingBinding &&
      organisations.length === 1 &&
      only &&
      !bind.isPending &&
      !bind.isSuccess &&
      !bind.error
    ) {
      bind.mutate(only.id);
    }
  }, [
    bind.error,
    bind.isPending,
    bind.isSuccess,
    bind.mutate,
    missingBinding,
    organisations,
  ]);

  if (roster.isPending || (missingBinding && bind.isPending)) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Preparing this shared device" />
      </main>
    );
  }

  if (roster.data) {
    if (belongsHere) {
      if (enroll.isPending || !enroll.isSuccess) {
        if (enroll.error) {
          return (
            <main className="grid min-h-dvh place-items-center bg-canvas p-5">
              <ErrorState
                message={displayError(enroll.error)}
                onAction={() => enroll.mutate()}
                title="This account could not be added to the device"
              />
            </main>
          );
        }
        return (
          <main className="grid min-h-dvh place-items-center bg-canvas p-5">
            <LoadingState label="Adding your account to this device" />
          </main>
        );
      }
      return children(scopeMeToOrganisation(me, roster.data.org_id));
    }
    return (
      <GateFrame>
        <LockKeyhole className="size-7 text-emerald-800" aria-hidden="true" />
        <h1 className="mt-5 text-2xl font-black">This device belongs to {roster.data.org_name}</h1>
        <p className="mt-2 text-sm leading-6 text-stone-600">
          Your account is not linked to that workplace. Remove the workplace
          from this browser, then sign in again to bind the right one.
        </p>
        {remove.error ? <p className="mt-4 text-sm text-red-800">{displayError(remove.error)}</p> : null}
        <Button className="mt-6" disabled={remove.isPending} onClick={() => remove.mutate()}>
          Use another workplace
        </Button>
      </GateFrame>
    );
  }

  if (!missingBinding) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <ErrorState
          message={displayError(roster.error)}
          onAction={() => void roster.refetch()}
          title="This shared device could not be prepared"
        />
      </main>
    );
  }

  if (organisations.length === 1) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        {bind.error ? (
          <ErrorState
            message={displayError(bind.error)}
            onAction={() => bind.mutate(organisations[0]!.id)}
            title="This browser could not be linked"
          />
        ) : (
          <LoadingState label="Linking this browser to your workplace" />
        )}
      </main>
    );
  }

  return (
    <GateFrame>
      <Building2 className="size-7 text-emerald-800" aria-hidden="true" />
      <h1 className="mt-5 text-2xl font-black">Choose this device’s workplace</h1>
      <p className="mt-2 text-sm leading-6 text-stone-600">
        Everyone shown on the quick lock screen will come from this workplace.
        You can remove it later to use a different organisation.
      </p>
      <div className="mt-6 space-y-2">
        {organisations.map((organisation) => (
          <Button
            className="w-full justify-start"
            disabled={bind.isPending}
            key={organisation.id}
            onClick={() => bind.mutate(organisation.id)}
            variant="outline"
          >
            <Building2 className="size-4" aria-hidden="true" />
            {organisation.name}
          </Button>
        ))}
      </div>
      {bind.error ? <p className="mt-4 text-sm text-red-800">{displayError(bind.error)}</p> : null}
    </GateFrame>
  );
}

function GateFrame({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5">
      <div className="w-full max-w-lg">
        <div className="mb-8"><Brand /></div>
        <Card><CardContent className="p-7">{children}</CardContent></Card>
      </div>
    </main>
  );
}
